/**
 * 일지 작성 / 수정
 * ---------------------------------------------------------------------------
 * 세 서식을 한 편집기로 다룬다. 유형에 따라 본문 구역만 바꾼다.
 *
 * 유형은 주소에서 온다
 *   메뉴가 유형별로 나뉘어 있다. 화면 안에서 다시 고르게 하면 메뉴로 들어온
 *   뜻과 어긋나고, 뒤로 가기를 눌렀을 때 무엇을 보고 있었는지도 잃는다.
 *
 * 새 일지는 사업장·날짜를 고르면 서버의 `/daily-logs/open` 이 답한다.
 *   - 이미 있으면 그 일지를 연다 (두 사람이 같은 날짜를 새로 만들지 않게)
 *   - 없으면 시설 마스터로 채운 빈 양식을 준다
 *
 * 날씨
 *   사업장 주소지 좌표로 그날의 값을 받아 채운다. 사람이 매일 손으로 적으면
 *   빠뜨리거나 어제 값을 그대로 둔다. 채운 뒤에는 고칠 수 있다 — 자동 입력이
 *   수정을 막으면 그때부터는 방해다.
 *
 * 상태는 DailyLogDetail 하나다. 유형별로 상태를 나누면 저장·복사·상태 전이
 * 코드를 세 벌 관리하게 된다.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  App as AntApp,
  Button,
  Card,
  DatePicker,
  Descriptions,
  InputNumber,
  Popconfirm,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
} from 'antd';
import {
  DeleteOutlined,
  LeftOutlined,
  PrinterOutlined,
  ReloadOutlined,
  RightOutlined,
} from '@ant-design/icons';
import dayjs, { type Dayjs } from 'dayjs';
import type {
  DailyLogCopyResult,
  DailyLogDetail,
  LogStatus,
  LogType,
  MaintenanceRow,
  Site,
  WeatherReading,
} from '@eco/shared';
import { ECO_PERMISSIONS, LOG_STATUS_LABELS, LOG_TYPES, LOG_TYPE_LABELS } from '@eco/shared';
import { api, errorMessage } from '../lib/api';
import { siteOptions } from '../lib/siteOptions';
import { printDailyLogs } from '../lib/print';
import { can, useSession } from '../lib/session';
import { WeatherIcon } from '../components/WeatherIcon';
import { AttachmentPanel } from '../components/AttachmentPanel';
import { HelpH, HelpList, HelpP, HelpPath, PageHelp } from '../components/PageHelp';
import { AirForm } from './daily-log/AirForm';
import { WaterConsignForm } from './daily-log/WaterConsignForm';
import { WaterSelfForm } from './daily-log/WaterSelfForm';
import { ApproverPicker } from './daily-log/ApproverPicker';
import { CopyDialog, type CopyPlan } from './daily-log/CopyDialog';
import { PrintDialog } from './daily-log/PrintDialog';
import { DateRangeCell, TextCell, appendRow, patchRow, removeRow } from './daily-log/rows';

/**
 * 직접 고를 수 있는 날씨.
 *
 * 자동으로 받은 값과 같은 낱말을 쓴다. 다른 낱말을 쓰면 같은 날씨가 날마다
 * 다른 글자로 남고, 아이콘도 되짚지 못한다.
 */
const WEATHER_OPTIONS: Array<{ value: string; code: number }> = [
  { value: '맑음', code: 0 },
  { value: '대체로 맑음', code: 1 },
  { value: '구름조금', code: 2 },
  { value: '흐림', code: 3 },
  { value: '안개', code: 45 },
  { value: '이슬비', code: 51 },
  { value: '비', code: 61 },
  { value: '소나기', code: 80 },
  { value: '눈', code: 71 },
  { value: '뇌우', code: 95 },
];

const STATUS_COLOR: Record<LogStatus, string> = {
  SUBMITTED: 'blue',
  CONFIRMED: 'green',
};

const EMPTY_MAINTENANCE: MaintenanceRow = {
  facilityId: null,
  facilityName: '',
  outletNo: null,
  startedAt: null,
  finishedAt: null,
  maintainer: null,
  details: null,
  sortOrder: 0,
};

export function DailyLogEditorPage() {
  const params = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { message, modal } = AntApp.useApp();
  const { data: session } = useSession();

  const canWrite = can(session, ECO_PERMISSIONS.DAILY_LOG_WRITE);
  const canConfirm = can(session, ECO_PERMISSIONS.DAILY_LOG_CONFIRM);
  const canPrint = can(session, ECO_PERMISSIONS.REPORT_EXPORT);
  const canReadDocs = can(session, ECO_PERMISSIONS.SELF_MEASURE_READ);

  const logType = (LOG_TYPES as readonly string[]).includes(params.logType ?? '')
    ? (params.logType as LogType)
    : 'T01_AIR';
  const routeId = params.dailyLogId;

  /*
    ---- 새 일지의 선택 조건 ----
    대시보드 달력에서 빠진 날을 누르고 오면 주소에 날짜와 사업장이 실려 온다
    (?date=2026-09-18&siteId=...). 그 날짜로 바로 양식을 열어야 한다 — 오늘로
    열리면 사용자는 다시 달력에서 고른 날을 찾아 바꿔야 한다.
  */
  const [searchParams] = useSearchParams();
  const [siteId, setSiteId] = useState<string | undefined>(
    () => searchParams.get('siteId') ?? undefined,
  );
  const [logDate, setLogDate] = useState<Dayjs>(() => {
    const requested = searchParams.get('date');
    // 주소는 사용자가 고칠 수 있다. 형식이 아니면 오늘로 연다.
    if (!requested || !/^\d{4}-\d{2}-\d{2}$/.test(requested)) return dayjs();
    const parsed = dayjs(requested);
    return parsed.isValid() ? parsed : dayjs();
  });

  /* ---- 편집 중인 내용 ---- */
  const [detail, setDetail] = useState<DailyLogDetail | null>(null);
  const [currentId, setCurrentId] = useState<string | null>(routeId ?? null);
  const [copyOpen, setCopyOpen] = useState(false);
  const [printOpen, setPrintOpen] = useState(false);
  const [printing, setPrinting] = useState(false);

  /**
   * 새 일지에서 이미 양식을 연 조합.
   *
   * 자동으로 여는 동작이 같은 조건에서 두 번 돌면 안 된다. StrictMode 는
   * 효과를 두 번 실행하고, 그러면 서버에 같은 요청이 두 번 간다.
   */
  const autoOpened = useRef<string | null>(null);

  /** 저장하지 않은 수정이 있는가. 날짜를 옮길 때 물어보기 위해 본다. */
  const dirty = useRef(false);

  // 메뉴로 다른 유형에 들어가면 주소만 바뀌고 컴포넌트는 그대로 살아 있다.
  // 이전 유형의 내용이 남아 있으면 엉뚱한 본문이 보인다.
  useEffect(() => {
    setCurrentId(routeId ?? null);
    if (!routeId) {
      setDetail(null);
      autoOpened.current = null;
    }
  }, [routeId, logType]);

  const sites = useQuery<Site[]>({
    queryKey: ['sites'],
    queryFn: async () => (await api.get<Site[]>('/sites')).data,
  });

  /**
   * 사업장을 미리 고른다.
   *
   * 대부분의 기업은 사업장이 하나다. 그 하나를 매번 고르게 하면 [일지 작성]
   * 을 누른 직후 화면에 빈 선택 상자와 비활성 버튼만 남아 "아무것도 안 나온다"
   * 가 된다. 여러 곳이면 첫 번째를 고르고, 사용자가 바꾸면 그때 다시 연다.
   */
  useEffect(() => {
    if (currentId || siteId) return;
    const first = sites.data?.[0];
    if (first) setSiteId(first.siteId);
  }, [sites.data, siteId, currentId]);

  /** 기존 일지를 여는 경우. */
  const existing = useQuery<DailyLogDetail>({
    queryKey: ['daily-log', currentId],
    queryFn: async () => (await api.get<DailyLogDetail>(`/daily-logs/${currentId}`)).data,
    enabled: !!currentId,
  });

  useEffect(() => {
    if (existing.data) {
      setDetail(existing.data);
      setSiteId(existing.data.siteId);
      setLogDate(dayjs(existing.data.logDate));
      // 서버에서 막 받아 온 내용이다. 아직 고친 것이 없다.
      dirty.current = false;
    }
  }, [existing.data]);

  /**
   * 편집 중 상태의 한 부분만 바꾼다.
   *
   * 고친 적이 있는지 기록해 둔다. 날짜를 옮길 때 저장하지 않은 내용이
   * 있으면 물어봐야 한다.
   */
  const patch = (part: Partial<DailyLogDetail>) => {
    dirty.current = true;
    setDetail((d) => (d ? { ...d, ...part } : d));
  };

  /**
   * 사업장 주소지 기준 날씨를 받아 채운다.
   *
   * 실패해도 오류로 알리지 않는다. 날씨를 못 받았다고 일지를 못 쓰게 할 수는
   * 없고, 사용자가 직접 적으면 된다. 버튼으로 부른 경우에만 결과를 알린다.
   */
  const fetchWeather = async (options: { announce: boolean }) => {
    if (!siteId) return;
    try {
      const res = await api.get<WeatherReading | null>('/daily-logs/weather/lookup', {
        params: { siteId, date: logDate.format('YYYY-MM-DD') },
      });
      const reading = res.data;
      if (!reading) {
        if (options.announce) {
          message.warning('날씨를 가져오지 못했습니다. 사업장 주소를 확인하거나 직접 입력해 주세요.');
        }
        return;
      }
      patch({
        weather: reading.weather,
        weatherCode: reading.weatherCode,
        temperature: reading.temperature,
      });
      if (options.announce) message.success('날씨를 다시 받았습니다.');
    } catch (err) {
      if (options.announce) message.error(errorMessage(err, '날씨를 가져오지 못했습니다.'));
    }
  };

  /** 새 일지: 서버에 "이 날짜에 일지가 있나" 를 물어본다. */
  const open = useMutation({
    mutationFn: async () => {
      const res = await api.get<{
        existing: DailyLogDetail | null;
        template: DailyLogDetail | null;
      }>('/daily-logs/open', {
        params: { siteId, logType, logDate: logDate.format('YYYY-MM-DD') },
      });
      return res.data;
    },
    onSuccess: async (data) => {
      if (data.existing) {
        message.info('해당 날짜의 일지가 이미 있어 그 일지를 엽니다.');
        setCurrentId(data.existing.dailyLogId);
        navigate(`/logs/${logType}/${data.existing.dailyLogId}`, { replace: true });
        return;
      }
      if (data.template) {
        setDetail(data.template);
        if (isTemplateEmpty(data.template)) {
          message.warning('등록된 시설이 없습니다. 사업장·시설 화면에서 먼저 등록해 주세요.');
        }
        // 양식을 연 직후에 채운다. 저장 전에 사용자가 확인하고 고칠 수 있다.
        await fetchWeather({ announce: false });
      }
    },
    onError: (err) => message.error(errorMessage(err)),
  });

  /**
   * 새 일지 화면은 비어 있으면 안 된다.
   *
   * 사업장·유형·날짜가 정해지면 곧바로 양식을 연다. 예전에는 [양식 열기] 를
   * 눌러야 했는데, [일지 작성] 을 누른 사람은 이미 "쓰겠다" 고 말한 것이다.
   * 한 번 더 누르게 하면서 그때까지 빈 화면을 보여 줄 이유가 없다.
   *
   * 조합마다 한 번만 연다. 사업장이나 날짜를 바꾸면 그 조합으로 다시 연다.
   */
  const openForm = open.mutate;
  useEffect(() => {
    if (currentId || !siteId) return;

    const key = `${siteId}|${logType}|${logDate.format('YYYY-MM-DD')}`;
    if (autoOpened.current === key) return;

    autoOpened.current = key;
    openForm();
  }, [currentId, siteId, logType, logDate, openForm]);

  const save = useMutation({
    mutationFn: async () => {
      if (!detail || !siteId) throw new Error('사업장을 고르세요');

      // 유형에 맞는 본문만 보낸다. 서버가 섞인 본문을 거절하기도 하고,
      // 보내 봐야 버려지는 데이터로 요청을 키울 이유도 없다.
      const body: Record<string, unknown> = {
        siteId,
        logType,
        logDate: logDate.format('YYYY-MM-DD'),
        weather: detail.weather,
        weatherCode: detail.weatherCode,
        temperature: detail.temperature,
        note: detail.note,
        // 배열 순서가 결재란 순서다.
        approverUserIds: detail.approvers.map((a) => a.userId),
        // 보수 칸이 없는 서식(폐수 위탁·폐수 자가)은 예전에 적어 둔 것이 있어도
        // 인쇄되지 않고 화면에서도 고칠 수 없으니 남기지 않는다.
        maintenances: hasMaintenance(logType) ? detail.maintenances : [],
      };

      if (logType === 'T01_AIR') {
        body.airOutlets = detail.airOutlets;
        body.airPreventions = detail.airPreventions;
      } else if (logType === 'T02_WATER_CONSIGN') {
        body.waterConsign = detail.waterConsign;
        body.consignMaterials = detail.consignMaterials;
      } else {
        body.waterSelf = detail.waterSelf;
        body.facilityHours = detail.facilityHours;
        body.preventionHours = detail.preventionHours;
        body.waterUsages = detail.waterUsages;
        body.sludges = detail.sludges;
        body.selfMaterials = detail.selfMaterials;
        body.chemicals = detail.chemicals;
        body.analyses = detail.analyses;
        body.autoChecks = detail.autoChecks;
      }

      if (currentId) {
        await api.put(`/daily-logs/${currentId}`, body);
        return currentId;
      }
      const res = await api.post<{ dailyLogId: string }>('/daily-logs', body);
      return res.data.dailyLogId;
    },
    onSuccess: async (id) => {
      message.success('저장했습니다.');
      dirty.current = false;
      setCurrentId(id);
      navigate(`/logs/${logType}/${id}`, { replace: true });
      await queryClient.invalidateQueries({ queryKey: ['daily-log', id] });
      await queryClient.invalidateQueries({ queryKey: ['daily-logs'] });
    },
    onError: (err) => message.error(errorMessage(err)),
  });

  const transition = useMutation({
    mutationFn: async (action: 'confirm' | 'unconfirm') => {
      const res = await api.post<{ allConfirmed?: boolean }>(
        `/daily-logs/${currentId}/${action}`,
      );
      return { action, allConfirmed: res.data?.allConfirmed ?? false };
    },
    onSuccess: async ({ action, allConfirmed }) => {
      if (action === 'unconfirm') message.success('확인을 해제했습니다.');
      else if (allConfirmed) message.success('확인했습니다. 확인자 전원이 확인해 확인완료입니다.');
      else message.success('확인했습니다. 남은 확인자가 있어 아직 작성완료입니다.');

      await queryClient.invalidateQueries({ queryKey: ['daily-log', currentId] });
      await queryClient.invalidateQueries({ queryKey: ['daily-logs'] });
    },
    onError: (err) => message.error(errorMessage(err)),
  });

  const copy = useMutation({
    mutationFn: async (plan: CopyPlan) => {
      const res = await api.post<DailyLogCopyResult>('/daily-logs/copy', {
        sourceDailyLogId: currentId,
        targetDates: plan.targetDates,
        applyWeather: plan.applyWeather,
      });
      return res.data;
    },
    onSuccess: async (result) => {
      setCopyOpen(false);

      const duplicated = result.skipped.filter((s) => s.reason === 'DUPLICATE').length;
      const failed = result.skipped.filter((s) => s.reason === 'FAILED');

      if (result.created.length > 0) {
        const withWeather = result.created.filter((c) => c.weatherApplied).length;
        message.success(
          `${result.created.length}건을 복사했습니다.` +
            (withWeather > 0 ? ` (${withWeather}건은 대상일의 날씨를 반영했습니다)` : ''),
        );
      }
      if (duplicated > 0) message.info(`${duplicated}건은 이미 일지가 있어 건너뛰었습니다.`);
      if (failed[0]) {
        message.error(`${failed.length}건을 만들지 못했습니다: ${failed[0].message ?? ''}`);
      }
      if (result.created.length === 0 && duplicated === 0 && failed.length === 0) {
        message.info('복사한 일지가 없습니다.');
      }

      await queryClient.invalidateQueries({ queryKey: ['daily-logs'] });
    },
    onError: (err) => message.error(errorMessage(err)),
  });

  /**
   * 사업장이나 날짜를 바꾼다.
   *
   * 지금 열려 있는 일지를 놓고 `/new` 로 옮긴다. 그러면 자동 열기 효과가
   * 새 조합으로 다시 돌아 그 날짜의 일지를 찾거나 빈 양식을 준다.
   *
   * 저장하지 않은 내용은 사라진다. 그래서 확인을 한 번 받는다 — 하루치를
   * 입력하다 날짜를 잘못 누르면 그 내용을 되찾을 방법이 없다.
   */
  const move = (next: { siteId?: string; logDate?: Dayjs }) => {
    const apply = () => {
      if (next.siteId !== undefined) setSiteId(next.siteId);
      if (next.logDate !== undefined) setLogDate(next.logDate);
      setDetail(null);
      setCurrentId(null);
      autoOpened.current = null;
      if (routeId) navigate(`/logs/${logType}/new`, { replace: true });
    };

    if (dirty.current) {
      modal.confirm({
        title: '저장하지 않은 내용이 있습니다',
        content: '이동하면 입력한 내용이 사라집니다. 이동할까요?',
        okText: '이동',
        cancelText: '취소',
        onOk: apply,
      });
      return;
    }
    apply();
  };

  const remove = useMutation({
    mutationFn: async () => api.delete(`/daily-logs/${currentId}`),
    onSuccess: async () => {
      message.success('삭제했습니다.');
      await queryClient.invalidateQueries({ queryKey: ['daily-logs'] });
      navigate(`/logs/${logType}`);
    },
    onError: (err) => message.error(errorMessage(err)),
  });

  const print = async (selfMeasurementId: string | null) => {
    if (!currentId) return;
    setPrinting(true);
    const failure = await printDailyLogs({ dailyLogIds: [currentId], selfMeasurementId });
    setPrinting(false);
    if (failure) message.error(failure);
    else setPrintOpen(false);
  };

  const locked = detail?.status === 'CONFIRMED' || !canWrite;

  const siteChoices = useMemo(
    () => siteOptions(sites.data),
    [sites.data],
  );

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card
        title={LOG_TYPE_LABELS[logType]}
        extra={
          <Space wrap>
            <PageHelp title={`${LOG_TYPE_LABELS[logType]} 작성 — 화면 설명`}>
              <HelpP>
                하루치 기록을 적는 화면입니다. 사업장과 날짜를 고르면 그 사업장에 등록된
                시설로 표가 채워집니다.
              </HelpP>

              <HelpH>표에 시설이 안 보인다면</HelpH>
              <HelpP>
                <HelpPath>사업장·시설 → 사업장 시설관리</HelpPath> 에서 먼저 등록하세요.
                등록하지 않으면 표가 빈 채로 열리고, 매일 손으로 시설명을 적어야 합니다.
              </HelpP>

              <HelpH>저장 · 제출 · 확인</HelpH>
              <HelpList
                items={[
                  { term: '저장', desc: '작성중 상태로 보관합니다. 언제든 다시 고칠 수 있습니다.' },
                  { term: '제출', desc: '확인자에게 넘깁니다.' },
                  {
                    term: '확인',
                    desc: '지정된 확인자 본인만 누를 수 있습니다. 확인 후에는 「확인 해제」를 해야 고칠 수 있습니다.',
                  },
                ]}
              />

              <HelpH>확인자는 WORK&ECO 사용자만</HelpH>
              <HelpP>
                회사 전체 구성원이 아니라 <b>WORK&ECO 를 쓰는 사람</b>만 고를 수 있습니다.
                ECO 에 들어올 수 없는 사람을 넣으면 그 사람은 확인을 할 수 없어 일지가
                확인 대기로 남습니다. 찾는 사람이 안 보이면 WORK&Hub 에서 그 구성원에게
                ECO 역할을 먼저 주세요.
              </HelpP>

              <HelpH>날씨</HelpH>
              <HelpP>
                사업장 주소를 기준으로 자동으로 받아옵니다. 주소가 비어 있으면 받아오지
                못하므로 사업장 정보에 주소를 넣어 두세요.
              </HelpP>

              {logType === 'T01_AIR' && (
                <>
                  <HelpH>자가측정 성적서</HelpH>
                  <HelpP>
                    「3. 자가측정사항」에 붙일 성적서는{' '}
                    <HelpPath>자가측정 서류관리</HelpPath> 메뉴에서 등록합니다. 인쇄할 때
                    등록된 것 중에서 고릅니다.
                  </HelpP>
                </>
              )}
            </PageHelp>

            <Button onClick={() => navigate(`/logs/${logType}`)}>목록</Button>
            {currentId && canPrint && (
              <Button
                icon={<PrinterOutlined />}
                loading={printing}
                // 자가측정 성적서는 대기 운영기록부에만 붙는다. 다른 서식은
                // 고를 것이 없으니 바로 인쇄한다.
                onClick={() => (logType === 'T01_AIR' ? setPrintOpen(true) : void print(null))}
              >
                인쇄
              </Button>
            )}
            {/*
              확인자 본인만 누를 수 있다. 서버가 판정하므로 여기서는 확인
              권한과 상태만 보고, 지정되지 않은 사람에게는 서버가 그렇게
              답한다 — 화면에서 감추면 "왜 버튼이 없지" 가 된다.
            */}
            {currentId && detail?.status === 'SUBMITTED' && canConfirm && (
              <Button
                type="primary"
                onClick={() => transition.mutate('confirm')}
                loading={transition.isPending}
              >
                확인자 확인하기
              </Button>
            )}
            {currentId && detail?.status === 'CONFIRMED' && canConfirm && (
              <Popconfirm
                title="확인 해제"
                description="해제하면 다시 수정할 수 있습니다."
                okText="해제"
                cancelText="취소"
                onConfirm={() => transition.mutate('unconfirm')}
              >
                <Button danger>확인 해제</Button>
              </Popconfirm>
            )}
            {currentId && canWrite && (
              <Button onClick={() => setCopyOpen(true)}>다른 날짜로 복사</Button>
            )}
            {currentId && canWrite && detail?.status !== 'CONFIRMED' && (
              <Popconfirm
                title="일지 삭제"
                description="이 일지와 입력한 모든 내용이 지워집니다. 되돌릴 수 없습니다."
                okText="삭제"
                okButtonProps={{ danger: true }}
                cancelText="취소"
                onConfirm={() => remove.mutate()}
              >
                <Button danger icon={<DeleteOutlined />} loading={remove.isPending}>
                  삭제
                </Button>
              </Popconfirm>
            )}
            {!locked && detail && (
              <Button type="primary" onClick={() => save.mutate()} loading={save.isPending}>
                저장
              </Button>
            )}
          </Space>
        }
      >
        <Space wrap size={12} style={{ marginBottom: detail ? 16 : 0 }}>
          {/*
            사업장과 날짜는 언제나 바꿀 수 있다.
            예전에는 일지를 한 번 열면 잠겼고, 다른 날짜를 보려면 목록으로
            나갔다 들어와야 했다. 날짜를 바꾸면 그 날짜의 일지를 찾아 열고,
            없으면 새 양식을 준다 — 서버의 /daily-logs/open 이 하는 일이다.
          */}
          <Select
            placeholder="사업장"
            style={{ width: 240 }}
            value={siteId}
            onChange={(v) => move({ siteId: v })}
            options={siteChoices}
          />
          <DatePicker
            value={logDate}
            allowClear={false}
            onChange={(v) => {
              if (v) move({ logDate: v });
            }}
          />
          <Tooltip title="전날 일지로 이동">
            <Button
              icon={<LeftOutlined />}
              onClick={() => move({ logDate: logDate.subtract(1, 'day') })}
            />
          </Tooltip>
          <Tooltip title="다음날 일지로 이동">
            <Button
              icon={<RightOutlined />}
              onClick={() => move({ logDate: logDate.add(1, 'day') })}
            />
          </Tooltip>
          <Button onClick={() => move({ logDate: dayjs() })}>오늘</Button>
          {detail && (
            <Tag color={STATUS_COLOR[detail.status]}>{LOG_STATUS_LABELS[detail.status]}</Tag>
          )}
        </Space>

        {/*
          사업장이 없으면 일지를 쓸 수 없다. 빈 선택 상자만 두면 무엇이
          잘못됐는지 알 수 없어 "눌러도 아무것도 안 나온다" 가 된다.
        */}
        {!currentId && sites.isSuccess && (sites.data ?? []).length === 0 && (
          <Alert
            type="warning"
            showIcon
            message="등록된 사업장이 없습니다"
            description="일지는 사업장에 속합니다. 사업장을 먼저 등록해 주세요."
            action={
              <Button size="small" type="primary" onClick={() => navigate('/sites')}>
                사업장 등록
              </Button>
            }
          />
        )}

        {!currentId && !detail && open.isPending && (
          <div style={{ padding: '24px 0', textAlign: 'center' }}>
            <Spin tip="양식을 불러오는 중" />
          </div>
        )}

        {detail && (
          <Descriptions bordered size="small" column={{ xs: 1, sm: 2, xl: 4 }}>
            <Descriptions.Item label="날씨">
              <Space.Compact style={{ width: '100%' }}>
                <span style={{ padding: '0 8px', display: 'inline-flex', alignItems: 'center' }}>
                  <WeatherIcon code={detail.weatherCode} label={detail.weather} />
                </span>
                <Select
                  allowClear
                  size="small"
                  style={{ flex: 1, minWidth: 110 }}
                  value={detail.weather ?? undefined}
                  disabled={locked}
                  options={WEATHER_OPTIONS.map((o) => ({ value: o.value, label: o.value }))}
                  onChange={(v) =>
                    // 글자를 직접 고르면 코드도 맞춰 준다. 아이콘이 글자와
                    // 어긋난 채 남는 것을 막는다.
                    patch({
                      weather: v ?? null,
                      weatherCode: WEATHER_OPTIONS.find((o) => o.value === v)?.code ?? null,
                    })
                  }
                />
                <Tooltip title="사업장 주소지 기준으로 다시 받기">
                  <Button
                    size="small"
                    icon={<ReloadOutlined />}
                    disabled={locked || !siteId}
                    onClick={() => void fetchWeather({ announce: true })}
                  />
                </Tooltip>
              </Space.Compact>
            </Descriptions.Item>
            <Descriptions.Item label="기온(℃)">
              <InputNumber
                size="small"
                style={{ width: '100%' }}
                value={detail.temperature ?? undefined}
                disabled={locked}
                onChange={(v) => patch({ temperature: v ?? null })}
              />
            </Descriptions.Item>
            <Descriptions.Item label="작성자">{detail.writerName ?? '-'}</Descriptions.Item>
            <Descriptions.Item label="확인자">{detail.confirmerName ?? '-'}</Descriptions.Item>
          </Descriptions>
        )}
      </Card>

      {detail && logType === 'T01_AIR' && (
        <AirForm
          outlets={detail.airOutlets}
          preventions={detail.airPreventions}
          locked={locked}
          onOutlets={(airOutlets) => patch({ airOutlets })}
          onPreventions={(airPreventions) => patch({ airPreventions })}
        />
      )}

      {detail && logType === 'T02_WATER_CONSIGN' && detail.waterConsign && (
        <WaterConsignForm
          body={detail.waterConsign}
          materials={detail.consignMaterials}
          locked={locked}
          onBody={(waterConsign) => patch({ waterConsign })}
          onMaterials={(consignMaterials) => patch({ consignMaterials })}
        />
      )}

      {detail && logType === 'T03_WATER_SELF' && detail.waterSelf && (
        <WaterSelfForm
          locked={locked}
          sections={{
            body: detail.waterSelf,
            facilityHours: detail.facilityHours,
            preventionHours: detail.preventionHours,
            waterUsages: detail.waterUsages,
            sludges: detail.sludges,
            materials: detail.selfMaterials,
            chemicals: detail.chemicals,
            analyses: detail.analyses,
            autoChecks: detail.autoChecks,
          }}
          handlers={{
            onBody: (waterSelf) => patch({ waterSelf }),
            onFacilityHours: (facilityHours) => patch({ facilityHours }),
            onPreventionHours: (preventionHours) => patch({ preventionHours }),
            onWaterUsages: (waterUsages) => patch({ waterUsages }),
            onSludges: (sludges) => patch({ sludges }),
            onMaterials: (selfMaterials) => patch({ selfMaterials }),
            onChemicals: (chemicals) => patch({ chemicals }),
            onAnalyses: (analyses) => patch({ analyses }),
            onAutoChecks: (autoChecks) => patch({ autoChecks }),
          }}
        />
      )}

      {detail && (
        <ApproverPicker
          approvers={detail.approvers}
          siteId={siteId}
          locked={locked}
          onChange={(approvers) => patch({ approvers })}
        />
      )}

      {detail && hasMaintenance(logType) && (
        <Card
          // 서식의 이름 그대로 쓴다. T01 은 「2. 방지시설 운영사항」 아래
          // 두 번째 표이고, 나머지 서식에서는 독립된 보수 기록이다.
          title={logType === 'T01_AIR' ? '나. 방지시설 보수사항' : '시설 보수사항'}
          size="small"
          extra={
            !locked && (
              <Button
                size="small"
                onClick={() =>
                  patch({ maintenances: appendRow(detail.maintenances, EMPTY_MAINTENANCE) })
                }
              >
                행 추가
              </Button>
            )
          }
        >
          <Table<MaintenanceRow>
            className="eco-grid"
            rowKey={(_, i) => String(i)}
            size="small"
            pagination={false}
            scroll={{ x: 1250 }}
            dataSource={detail.maintenances}
            locale={{ emptyText: '보수 내역이 없으면 비워 둡니다.' }}
            columns={[
              {
                // 시설명은 길다. "2호기 집진기 후단 백필터" 같은 이름이 흔하다.
                title: logType === 'T01_AIR' ? '방지시설명' : '시설명',
                width: 400,
                render: (_, row, i) => (
                  <TextCell
                    value={row.facilityName}
                    disabled={locked}
                    onChange={(v) =>
                      patch({
                        maintenances: patchRow(detail.maintenances, i, { facilityName: v ?? '' }),
                      })
                    }
                  />
                ),
              },
              {
                // 서식의 「배 출 구 별」. 마스터에서 온 보수 행이 없으므로
                // 언제나 직접 적는다.
                title: '배 출 구 별',
                width: 110,
                render: (_, row, i) => (
                  <TextCell
                    value={row.outletNo}
                    disabled={locked}
                    maxLength={20}
                    onChange={(v) =>
                      patch({ maintenances: patchRow(detail.maintenances, i, { outletNo: v }) })
                    }
                  />
                ),
              },
              {
                // 서식은 기간을 한 칸에 년월일로 받는다. 시각 칸이 없다.
                title: '보 수 기 간',
                width: 250,
                render: (_, row, i) => (
                  <DateRangeCell
                    start={row.startedAt}
                    end={row.finishedAt}
                    disabled={locked}
                    onChange={(startedAt, finishedAt) =>
                      patch({
                        maintenances: patchRow(detail.maintenances, i, { startedAt, finishedAt }),
                      })
                    }
                  />
                ),
              },
              {
                title: '보 수 자',
                width: 130,
                render: (_, row, i) => (
                  <TextCell
                    value={row.maintainer}
                    disabled={locked}
                    maxLength={100}
                    onChange={(v) =>
                      patch({ maintenances: patchRow(detail.maintenances, i, { maintainer: v }) })
                    }
                  />
                ),
              },
              {
                // 보수 내용은 한 줄로 짧게 적는다. 자세한 것은 별도 보고서다.
                title: '보 수 명 세',
                width: 240,
                render: (_, row, i) => (
                  <TextCell
                    value={row.details}
                    disabled={locked}
                    maxLength={2000}
                    onChange={(v) =>
                      patch({ maintenances: patchRow(detail.maintenances, i, { details: v }) })
                    }
                  />
                ),
              },
              {
                title: '',
                width: 60,
                render: (_, __, i) =>
                  !locked && (
                    <Button
                      size="small"
                      type="link"
                      danger
                      onClick={() => patch({ maintenances: removeRow(detail.maintenances, i) })}
                    >
                      삭제
                    </Button>
                  ),
              },
            ]}
          />
        </Card>
      )}

      {/*
        증빙 첨부.
        일지를 저장한 뒤에만 붙일 수 있다 — 아직 없는 일지에 파일을 매달 수
        없다. 확인완료된 일지는 더 고칠 수 없으므로 목록과 내려받기만 남긴다.
      */}
      {detail && currentId && (
        <AttachmentPanel
          owner={{ dailyLogId: currentId }}
          disabled={locked}
          title="증빙 첨부"
        />
      )}

      {detail && currentId && (
        <>
          <CopyDialog
            open={copyOpen}
            sourceDate={detail.logDate}
            loading={copy.isPending}
            onCancel={() => setCopyOpen(false)}
            onConfirm={(plan) => copy.mutate(plan)}
          />
          <PrintDialog
            open={printOpen}
            count={1}
            siteId={detail.siteId}
            canChoose={canReadDocs}
            loading={printing}
            onCancel={() => setPrintOpen(false)}
            onConfirm={(id) => void print(id)}
          />
        </>
      )}
    </Space>
  );
}

/**
 * 시설이 하나도 안 잡힌 빈 양식인지. 유형마다 볼 곳이 다르다.
 *
 * 폐수 위탁은 사업장 단위 장부라 시설을 고르지 않는다. "등록된 시설이 없다"
 * 고 알리면 쓸 필요 없는 것을 등록하러 가게 된다.
 */
function isTemplateEmpty(t: DailyLogDetail): boolean {
  switch (t.logType) {
    case 'T01_AIR':
      return t.airOutlets.length === 0 && t.airPreventions.length === 0;
    case 'T02_WATER_CONSIGN':
      return false;
    case 'T03_WATER_SELF':
      return t.facilityHours.length === 0 && t.preventionHours.length === 0;
  }
}

/**
 * 서식에 보수 칸이 있는가.
 *
 * 대기(별지 제7호서식)에만 「나. 방지시설 보수사항」이 있다. 폐수 위탁(제20호)
 * 과 폐수 자가(제18호)에는 보수를 적는 칸이 없다 — 자가 서식에서는 고장 사실을
 * 「9. 수질오염방지시설 고장 유무 및 특기사항」에 적는다.
 */
function hasMaintenance(type: LogType): boolean {
  return type === 'T01_AIR';
}
